// The consent runtime: what loads, when, and what it is told.
//
// Google consent mode in its basic form. Neither the Google tag, nor its
// dataLayer, nor the Metricool pixel exists on the page until analytics is
// allowed, so nothing that happened before can be queued and replayed after:
// an event fired too early is simply dropped. Once loaded, Google is told the
// visitor's four consent states and kept up to date as they change.
//
// Only the public site is measured, and only through addresses this file has
// cleaned. Admin and signed-in pages carry client names and ids in their URLs,
// and GA sends the raw address with the page views it makes on its own when the
// SPA navigates (asynchronously, so nothing can intercept them in flight). So a
// page that enters any of those routes switches GA off for the rest of its life,
// before the navigation happens, and the page views GA does receive are sent
// from here with a URL reduced to the parameters listed below.

import { LEGAL_PAGES } from '@wanderlust/shared';
import {
  GA_COOKIE_MAX_AGE_SECONDS,
  GA_MEASUREMENT_ID,
  METRICOOL_HASH,
  TRACKED_HOSTS,
} from './analyticsConfig';
import { googleConsentState, type ConsentChoices } from './consent';
import { isInternalTraffic } from './internalTraffic';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    beTracker?: { t: (params: Record<string, string>) => void };
  }
}

export const GA_DISABLE_KEY = `ga-disable-${GA_MEASUREMENT_ID}`;
export const GTAG_SRC = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
export const METRICOOL_SRC = 'https://tracker.metricool.com/resources/be.js';

/**
 * The public site. Everything else (admin, the signed-in app, sign-in) is never
 * measured. The legal pages are here so that opening one from a footer does
 * not count as leaving the public site, which would switch GA off for the rest
 * of the page's life.
 */
const MEASURED_PATHS = [
  '/',
  '/plan',
  '/blog',
  '/newsletter',
  '/traveler-stories',
  '/fges',
  '/fgesturkeytrot2026',
  ...LEGAL_PAGES.map((page) => page.path),
];

export function isMeasuredPath(pathname: string): boolean {
  const path = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  return MEASURED_PATHS.includes(path) || path.startsWith('/blog/');
}

/**
 * The query parameters a measured URL keeps: campaign tags (which GA reads for
 * attribution), the two WanderNerd sends, the itinerary a /plan visit started
 * from, and the blog's filters. Everything else is dropped, `?dream=` included,
 * since that token leads back to what somebody typed.
 */
const KEPT_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
  'tool',
  'result',
  'template',
  'variant',
  'tag',
  'category',
  'page',
];

/** A page's address as GA receives it: no fragment, and only the kept parameters. */
export function measuredUrl(href: string): string {
  const url = new URL(href);
  const kept = new URLSearchParams();
  for (const key of KEPT_PARAMS) {
    const value = url.searchParams.get(key);
    if (value !== null) kept.set(key, value.slice(0, 100));
  }
  const query = kept.toString();
  return `${url.origin}${url.pathname}${query ? `?${query}` : ''}`;
}

/**
 * The referrer as GA receives it. Another site is reduced to its origin, which
 * is all attribution needs. One of our own pages is cleaned like any measured
 * URL, or dropped if it is not one: a full page load out of the admin would
 * otherwise carry the admin's address along.
 */
export function measuredReferrer(referrer: string, origin: string): string {
  if (!referrer) return '';
  let url: URL;
  try {
    url = new URL(referrer);
  } catch {
    return '';
  }
  if (url.origin !== origin) return `${url.origin}/`;
  return isMeasuredPath(url.pathname) ? measuredUrl(referrer) : '';
}

/** Parameters that tie an address to one of our own records. Metricool, which cannot be given a cleaned URL, is not loaded on a page that carries one. */
const RECORD_PARAMS = /[?&](token|invite|dream)=/;

interface TrackingState {
  started: boolean;
  choices: ConsentChoices | null;
  gaLoaded: boolean;
  metricoolLoaded: boolean;
  /** Set once the page has been anywhere outside the public site. Never cleared. */
  leftPublicSite: boolean;
  /**
   * The page was opened from a link carrying a token (a newsletter unsubscribe,
   * an invitation). GA would record it in the address, and again as the next
   * page's referrer, so nothing runs on that page at all.
   */
  tokenPage: boolean;
  /** The router's key for the current location, and the one a page view was last sent for. */
  locationKey: string | null;
  sentForKey: string | null;
  /** The last measured URL sent, which is the next page view's referrer. */
  previousPage: string | null;
}

const initialState = (): TrackingState => ({
  started: false,
  choices: null,
  gaLoaded: false,
  metricoolLoaded: false,
  leftPublicSite: false,
  tokenPage: false,
  locationKey: null,
  sentForKey: null,
  previousPage: null,
});

let state: TrackingState = initialState();

/** Outside the state so a test reset does not wrap history a second time. */
let guardInstalled = false;

function liveHost(): boolean {
  return TRACKED_HOSTS.includes(window.location.hostname);
}

/** Whether this page may load trackers at all, whatever the visitor chose. */
function pageAllowsTracking(): boolean {
  return liveHost() && !state.tokenPage && !state.leftPublicSite;
}

/** Google's own off switch, which gtag checks before every event it sends. */
function gaShouldBeOff(): boolean {
  return !state.choices?.analytics || !pageAllowsTracking() || isInternalTraffic();
}

function syncGaSwitch() {
  if (!state.gaLoaded) return;
  (window as unknown as Record<string, unknown>)[GA_DISABLE_KEY] = gaShouldBeOff();
}

function gtag(...args: unknown[]) {
  window.gtag?.(...args);
}

function loadGoogleTag(choices: ConsentChoices) {
  state.gaLoaded = true;
  syncGaSwitch();
  const dataLayer = (window.dataLayer = window.dataLayer || []);
  // gtag.js reads its commands as Arguments objects, which is why this is
  // Google's function rather than a rest-parameter one.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments);
  };
  gtag('consent', 'default', googleConsentState(choices));
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    // Page views are sent from here, with a cleaned address.
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    cookie_expires: GA_COOKIE_MAX_AGE_SECONDS,
  });
  const script = document.createElement('script');
  script.async = true;
  script.src = GTAG_SRC;
  document.head.appendChild(script);
}

function loadMetricool() {
  state.metricoolLoaded = true;
  const script = document.createElement('script');
  script.src = METRICOOL_SRC;
  // One pixel for the page it loads on. It does not follow the SPA.
  script.onload = () => window.beTracker?.t({ hash: METRICOOL_HASH });
  document.head.appendChild(script);
}

function sendPageView() {
  if (!state.gaLoaded || gaShouldBeOff()) return;
  if (state.locationKey === null || state.sentForKey === state.locationKey) return;
  if (!isMeasuredPath(window.location.pathname)) return;
  const pageLocation = measuredUrl(window.location.href);
  const pageReferrer =
    state.previousPage ?? measuredReferrer(document.referrer, window.location.origin);
  // `set` rather than event parameters, so the events GA sends on its own
  // (scroll, outbound clicks) carry the cleaned address too.
  gtag('set', { page_location: pageLocation, page_referrer: pageReferrer, page_title: document.title });
  gtag('event', 'page_view');
  state.sentForKey = state.locationKey;
  state.previousPage = pageLocation;
}

/** Names of the cookies each purpose leaves behind. */
const ANALYTICS_COOKIE = /^(_ga|_ga_.+|_gid|_gat.*)$/;
const ADVERTISING_COOKIE = /^_gcl_.+$/;

function deleteCookies(pattern: RegExp) {
  const names = document.cookie
    .split(';')
    .map((part) => part.split('=')[0].trim())
    .filter((name) => pattern.test(name));
  if (names.length === 0) return;
  // GA writes to the widest domain it can, so try every one it could have used.
  const labels = window.location.hostname.split('.');
  const domains = [''];
  for (let i = 0; i < labels.length - 1; i++) domains.push(`.${labels.slice(i).join('.')}`);
  for (const name of names) {
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ''}`;
    }
  }
}

/** Remove what a purpose left behind once it is not allowed. */
export function clearTrackingCookies(choices: ConsentChoices = { analytics: false, advertising: false }) {
  if (!choices.analytics) deleteCookies(ANALYTICS_COOKIE);
  if (!choices.advertising) deleteCookies(ADVERTISING_COOKIE);
}

/** Note where a navigation is going before it happens. */
function noteDestination(pathname: string) {
  if (state.leftPublicSite || isMeasuredPath(pathname)) return;
  state.leftPublicSite = true;
  syncGaSwitch();
}

function installNavigationGuard() {
  if (guardInstalled) return;
  guardInstalled = true;
  for (const method of ['pushState', 'replaceState'] as const) {
    const original = history[method];
    history[method] = function (this: History, data: unknown, unused: string, url?: string | URL | null) {
      if (url !== undefined && url !== null) {
        try {
          noteDestination(new URL(String(url), window.location.href).pathname);
        } catch {
          // A URL the browser will refuse anyway.
        }
      }
      return original.call(this, data, unused, url);
    };
  }
  // Registered before gtag.js loads, so it runs before GA's own listener.
  window.addEventListener('popstate', () => noteDestination(window.location.pathname));
}

/**
 * Called once, before anything renders. Notes what the landing page rules out
 * and starts watching navigation. Loads nothing.
 */
export function startTracking() {
  if (state.started) return;
  state.started = true;
  state.tokenPage = /[?&](token|invite)=/.test(window.location.search);
  state.leftPublicSite = !isMeasuredPath(window.location.pathname);
  installNavigationGuard();
}

/**
 * Apply the visitor's choices: load what they allow and this page permits,
 * update Google with all four consent states, and switch off and clean up
 * whatever they have withdrawn.
 */
export function applyConsent(choices: ConsentChoices) {
  state.choices = { ...choices };
  if (state.gaLoaded) {
    gtag('consent', 'update', googleConsentState(choices));
  } else if (choices.analytics && pageAllowsTracking()) {
    loadGoogleTag(choices);
  }
  syncGaSwitch();
  clearTrackingCookies(choices);
  if (
    choices.analytics &&
    pageAllowsTracking() &&
    !state.metricoolLoaded &&
    !isInternalTraffic() &&
    isMeasuredPath(window.location.pathname) &&
    !RECORD_PARAMS.test(window.location.search)
  ) {
    loadMetricool();
  }
  // The page they are on when they allow it is counted from that moment.
  // Nothing from before is sent: it was never recorded.
  sendPageView();
}

/** Called by the router on every location, including the first. */
export function trackLocation(locationKey: string) {
  state.locationKey = locationKey;
  noteDestination(window.location.pathname);
  sendPageView();
}

/** Re-read the internal-traffic flag after it changes. */
export function refreshTracking() {
  syncGaSwitch();
}

/** Whether events may go to GA right now. */
export function analyticsActive(): boolean {
  return state.gaLoaded && !gaShouldBeOff();
}

/** For tests: forget everything, as a fresh page would. */
export function resetTrackingForTests() {
  state = initialState();
}
